export const NEWS_USER_PROPERTY_ID = 'news_user';
export const POLLS_USER_PROPERTY_ID = 'polls_user';
export const NEWS_COMMENTATOR_PROPERTY_ID = 'news_commentator';
export const EXPERT_USER_PROPERTY_ID = 'expert_user';

/**
 * 全プロパティIDに適用
 */
export type PropertyId = UserPropertyId | CommentatorPropertyId;

/**
 * ユーザーのプロパティIDに適用
 */
export type UserPropertyId = NewsUserPropertyId | PollsUserPropertyId | ExpertUserPropertyId;

/**
 * コメンテーターのプロパティIDに適用
 */
export type CommentatorPropertyId = NewsCommentatorPropertyId;

/**
 * ニュースのプロパティIDに適用
 */
export type NewsPropertyId = NewsUserPropertyId | NewsCommentatorPropertyId;

/**
 * みんなの意見のプロパティIDに適用
 */
export type PollsPropertyId = PollsUserPropertyId;

/**
 * エキスパートのプロパティIDに適用
 */
export type ExpertPropertyId = ExpertUserPropertyId;

/**
 * 各プロパティID
 * 基本的にはこれらの型のUnion型を使用し、特定のプロパティIDのみを対象にする処理の場合のみ使用する
 */
export type NewsUserPropertyId = typeof NEWS_USER_PROPERTY_ID;
export type NewsCommentatorPropertyId = typeof NEWS_COMMENTATOR_PROPERTY_ID;
export type PollsUserPropertyId = typeof POLLS_USER_PROPERTY_ID;
export type ExpertUserPropertyId = typeof EXPERT_USER_PROPERTY_ID;

const toArray = <T extends string>(values: { [K in T]: K }): ReadonlyArray<T> => {
  return Object.values(values);
};

/**
 * プロパティIDの配列。
 */
const propertyIds: ReadonlyArray<PropertyId> = toArray<PropertyId>({
  news_user: NEWS_USER_PROPERTY_ID,
  polls_user: POLLS_USER_PROPERTY_ID,
  news_commentator: NEWS_COMMENTATOR_PROPERTY_ID,
  expert_user: EXPERT_USER_PROPERTY_ID
});

/**
 * ユーザーのプロパティIDの配列。
 * TypeScriptではUnion型を動的に配列に変換する方法はサポートされていない。そのためUserPropertyIdと同期を取るよう
 * キーと値が一致するようなオブジェクトを渡すことでUserPropertyIdの配列を取得する関数を使用する。
 */
const userPropertyIds: ReadonlyArray<UserPropertyId> = toArray<UserPropertyId>({
  news_user: NEWS_USER_PROPERTY_ID,
  polls_user: POLLS_USER_PROPERTY_ID,
  expert_user: EXPERT_USER_PROPERTY_ID
});

/**
 * コメンテーターのプロパティIDの配列。
 */
const commentatorPropertyIds: ReadonlyArray<CommentatorPropertyId> = toArray<CommentatorPropertyId>({
  news_commentator: NEWS_COMMENTATOR_PROPERTY_ID
});

/**
 * みんなの意見のプロパティIDの配列。
 */
const pollsPropertyIds: ReadonlyArray<PollsPropertyId> = toArray<PollsPropertyId>({
  polls_user: POLLS_USER_PROPERTY_ID
});

/**
 * エキスパートのプロパティIDの配列。
 */
const expertPropertyIds: ReadonlyArray<ExpertPropertyId> = toArray<ExpertPropertyId>({
  expert_user: EXPERT_USER_PROPERTY_ID
});

/**
 * ユーザー用のPropertyIdかどうかを判定する
 *
 * @param propertyId プロパティID
 * @returns ユーザー用のPropertyIdであればtrueを返す
 */
export function isUserPropertyId(propertyId: string): propertyId is UserPropertyId {
  return userPropertyIds.includes(propertyId as UserPropertyId);
}

/**
 * コメンテーター用のPropertyIdかどうかを判定する
 *
 * @param propertyId プロパティID
 * @returns コメンテーター用のPropertyIdであればtrueを返す
 */
export function isCommentatorPropertyId(propertyId: string): propertyId is CommentatorPropertyId {
  return commentatorPropertyIds.includes(propertyId as CommentatorPropertyId);
}

/**
 * 存在するPropertyIdかどうかを判定する
 *
 * @param propertyId プロパティID
 * @returns 存在するPropertyIdであればtrueを返す
 */
export function isPropertyId(propertyId: string): propertyId is PropertyId {
  return propertyIds.includes(propertyId as PropertyId);
}

/**
 * みんなの意見用のPropertyIdかどうかを判定する
 *
 * @param propertyId プロパティID
 * @returns みんなの意見用のPropertyIdであればtrueを返す
 */
export function isPollsPropertyId(propertyId: string): propertyId is PollsPropertyId {
  return pollsPropertyIds.includes(propertyId as PollsPropertyId);
}

/**
 * エキスパート用のPropertyIdかどうかを判定する
 *
 * @param propertyId プロパティID
 * @returns エキスパート用のPropertyIdであればtrueを返す
 */
export function isExpertPropertyId(propertyId: string): propertyId is ExpertPropertyId {
  return expertPropertyIds.includes(propertyId as ExpertPropertyId);
}

export type ArticleType = 'news' | 'polls' | 'expert';

/**
 * プロパティIDを記事種別に変換する
 *
 * @param propertyId プロパティID
 */
export function getArticleType(propertyId: PropertyId): ArticleType {
  switch (propertyId) {
    case 'news_user':
    case 'news_commentator':
      return 'news';
    case 'polls_user':
      return 'polls';
    case 'expert_user':
      return 'expert';
    default: {
      const _: never = propertyId;
      return _;
    }
  }
}

/**
 * ArticleTypeをUserPropertyIdに変換する
 *
 * @param articleType 記事種別
 * @returns UserPropertyId
 */
export function getUserPropertyId(articleType: ArticleType): UserPropertyId {
  switch (articleType) {
    case 'news':
      return 'news_user';
    case 'polls':
      return 'polls_user';
    case 'expert':
      return 'expert_user';
    default: {
      const _: never = articleType;
      return _;
    }
  }
}
